import { toCalendarDayUtcIso } from '@/helpers/date';
import { computeNirKey } from '@/helpers/identity';
import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import { JobExperience } from '@/types/job';

import type { ProfilePreviewAgency } from '@/modules/worker/types';
import type { IAddress } from '@/types/address';
import type { IInterimJob } from '@/types/job';
import type {
	AddJobPayload,
	AddressPayload,
	FormattedProfile,
	SetNirPayload,
	UpdatePersonalInfoPayload,
} from './types';

export const normalizeProfilePreviewAgencies = (
	raw: unknown
): ProfilePreviewAgency[] => {
	if (!Array.isArray(raw)) return [];
	const seen = new Set<string>();
	const out: ProfilePreviewAgency[] = [];
	for (const item of raw) {
		if (!item || typeof item !== 'object') continue;
		const o = item as Record<string, unknown>;
		const rawId = o.id ?? o._id ?? o.agencyId;
		const id =
			typeof rawId === 'string' && rawId.trim()
				? rawId.trim()
				: typeof rawId === 'number' && Number.isFinite(rawId)
					? String(rawId)
					: '';
		if (!id || seen.has(id)) continue;
		seen.add(id);
		const rawName = o.publicName ?? o.name ?? o.agencyName;
		const name =
			typeof rawName === 'string' && rawName.trim()
				? rawName.trim()
				: typeof rawName === 'number' && Number.isFinite(rawName)
					? String(rawName)
					: id;
		out.push({ id, publicName: name });
	}
	return out;
};

const DOM_TOM_ZIP_PREFIX = '97';
const CORSICA_DEPARTMENT_CODE_PREFIX = '20';

/**
 * Derives French department code from a 5-digit postal code.
 * DOM-TOM use 3-digit department (971–976); métropole uses first 2 digits; Corsica (20xxx) maps to 2A.
 */
export const getDepartmentCodeFromZip = (zip: string): string => {
	const trimmed = (zip ?? '').trim();
	if (trimmed.length < 2) return '';
	if (trimmed.startsWith(DOM_TOM_ZIP_PREFIX) && trimmed.length >= 3)
		return trimmed.slice(0, 3);
	if (trimmed.startsWith(CORSICA_DEPARTMENT_CODE_PREFIX)) return '2A';
	return trimmed.slice(0, 2);
};

export type DepartmentRegionResolver = (zip: string) => {
	department: string;
	region: string;
};

/**
 * Converts frontend IAddress to backend AddressPayload.
 * Uses resolver when provided to fill department and region from ref data; otherwise derives department from zip.
 */
export const toAddressPayload = (
	address: IAddress,
	resolveDepartmentRegion?: DepartmentRegionResolver
): AddressPayload => {
	const zip = address.zip?.trim() ?? '';
	const { department, region } = resolveDepartmentRegion
		? resolveDepartmentRegion(zip)
		: { department: getDepartmentCodeFromZip(zip), region: '' };

	// IAddress.location is already GeoJSON ordered: [longitude, latitude]
	const [longitude, latitude] = address.location ?? [];
	const location =
		typeof longitude === 'number' && typeof latitude === 'number'
			? ([longitude, latitude] as [number, number])
			: undefined;

	return {
		street: address.street,
		complement: address.complement ?? '',
		city: address.city,
		zip: address.zip,
		department,
		region,
		countryCode: address.countryCode ?? 'FR',
		inseeCode: address.inseeCode ?? '',
		...(location && { location }),
	};
};

/**
 * Converts phone from display format ("06 12 34 56 78") to international format ("+33612345678").
 * Assumes French numbers if no country code is present.
 */
export const toInternationalPhone = (phone: string): string => {
	const digitsOnly = phone.replace(/\s/g, '').replace(/\D/g, '');

	if (digitsOnly.startsWith('0') && digitsOnly.length === 10) {
		// French number: 0X XX XX XX XX -> +33 X XX XX XX XX
		return `+33${digitsOnly.slice(1)}`;
	}

	if (digitsOnly.startsWith('33') && !digitsOnly.startsWith('0')) {
		return `+${digitsOnly}`;
	}

	if (digitsOnly.startsWith('+')) {
		return digitsOnly;
	}

	// Default: assume it's already in some format, just ensure + prefix if it looks international
	return digitsOnly.length > 10 ? `+${digitsOnly}` : `+33${digitsOnly}`;
};

/**
 * Converts birthDate from DD/MM/YYYY format to ISO 8601 UTC midnight string.
 * Falls back to the original string if parsing fails.
 */
export const toIsoBirthDate = (birthDate: string): string => {
	return toCalendarDayUtcIso(birthDate, 'DD/MM/YYYY') ?? birthDate;
};

/**
 * True when the updated address carries coordinates that differ from (or are missing in)
 * the current one, so re-selecting the same address can still add its geolocation.
 */
const hasNewLocation = (current: IAddress, updated: IAddress): boolean => {
	const [longitude, latitude] = updated.location ?? [];
	if (typeof longitude !== 'number' || typeof latitude !== 'number') {
		return false;
	}
	return (
		current.location?.[0] !== longitude || current.location?.[1] !== latitude
	);
};

/**
 * Compares two FormattedProfile objects and builds an UpdatePersonalInfoPayload
 * containing only the changed fields.
 * Optional resolveDepartmentRegion is used to fill address department/region from zip (e.g. via config store).
 */
export const toUpdatePersonalInfoPayload = (
	current: FormattedProfile,
	updated: FormattedProfile,
	options?: { resolveDepartmentRegion?: DepartmentRegionResolver }
): UpdatePersonalInfoPayload | null => {
	const payload: Partial<UpdatePersonalInfoPayload> = {};
	const resolveDepartmentRegion = options?.resolveDepartmentRegion;

	if (current.firstname !== updated.firstname) {
		payload.firstname = updated.firstname;
	}

	if (current.lastname !== updated.lastname) {
		payload.lastname = updated.lastname;
	}

	if (current.email !== updated.email) {
		payload.email = updated.email;
	}

	if (current.phone !== updated.phone) {
		payload.phone = toInternationalPhone(updated.phone);
	}

	if (current.birthDate !== updated.birthDate) {
		payload.birthday = toIsoBirthDate(updated.birthDate);
	}

	if (current.address !== updated.address) {
		// Compare addresses - if either is null, or if they differ
		const addressChanged =
			!current.address ||
			!updated.address ||
			current.address.street !== updated.address.street ||
			current.address.city !== updated.address.city ||
			current.address.zip !== updated.address.zip ||
			hasNewLocation(current.address, updated.address);

		if (addressChanged && updated.address) {
			payload.address = toAddressPayload(
				updated.address,
				resolveDepartmentRegion
			);
		}
	}

	// If no fields changed, return null
	if (Object.keys(payload).length === 0) {
		return null;
	}

	return payload as UpdatePersonalInfoPayload;
};

/**
 * Strips formatting from NIR to produce raw 13-char string, with its computed control key.
 */
export const toSetNirPayload = (nir: string): SetNirPayload => {
	const rawNir = nir.replace(/\s/g, '');
	return {
		nir: rawNir,
		nirKey: computeNirKey(rawNir) || undefined,
	};
};

const toBackendExperience = (experience: unknown): JobExperience =>
	normalizeJobExperience(experience) ?? JobExperience.NONE;

/**
 * Builds AddJobPayload from frontend job data.
 * Requires jobName to be resolved from the configuration store.
 */
export const toAddJobPayload = (
	job: IInterimJob,
	jobName: string
): AddJobPayload => {
	return {
		jobId: job.jobId,
		jobName,
		experience: toBackendExperience(job.experience),
	};
};
